<template>
  <v-container>
    <ViewsContainer
      :moduleName="moduleName"
      :listViewComponent="Table"
      :filterComponent="Filter"
      :data="data"
      :config="'@/components/modules/quotations/quotations.config'"
      :onCreate="create"
      :onUpdate="update"
      :exportCsv="exportCsv"
      :onDelete="remove"
      :show_toolbar="true"
      :show_pagination="true"
      :title="{singular:'Cotización', plural:'Cotizaciones'}"
      :rowActionHandlers="rowActionHandlers"
      :key="moduleName"
      :client="client"
      :clientId="clientId"
    />
  </v-container>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue';
import { usePreferencesStore } from '@/store/preferences';
import { useQuotationsStore } from '@/store/quotations';
import { useModuleLayoutFetcher } from '@/composables/useModuleLayoutFetcher';
import { HEADERS, DEFAULT_HEADER_ORDER, CONVERT_TO_SALE_FIELDS } from '@/components/modules/quotations/quotations.config';

import Table from '@/components/views/TableView.vue';
import MobileCardsView from '@/components/views/MobileCardsView.vue';
import Filter from '@/components/drawer/Filters.vue';
import ViewsContainer from '@/components/views/ViewsContainer.vue'
import { useFloatFormStore } from '@/store/floatForm';
import { CARD_FIELDS, DEFAULT_CARD_ORDER } from '@/components/modules/sales/sales.config';
import { useDisplay } from 'vuetify';

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

const moduleName = 'Quotations';
const preferencesStore = usePreferencesStore();
const quotationsStore = useQuotationsStore();
const floatFormStore = useFloatFormStore();

const props = defineProps<{ clientId: number, client?: any }>();

const { clientId } = props;

const buildParams = (params: any) => {
  // Solo agrega el filtro si clientId está definido y es válido
  if (clientId !== undefined && clientId !== null) {
    return {
      ...params,
      'filter[subject_id]': clientId,
      'filter[subject]': "clients",
    };
  }
  return params;
};

const {
  create,
  update,
  remove,
  exportCsv
} = useModuleLayoutFetcher(
  moduleName,
  (params) => quotationsStore.fetchQuotations(buildParams(params)),
  {
    create: quotationsStore.createQuotation,
    update: (data) => quotationsStore.updateQuotation(data.id, data),
    remove: quotationsStore.deleteQuotation,
    exportCsv: quotationsStore.exportCSV
  }
);

onMounted(() => {
  preferencesStore.currentModule = moduleName;
  // Registra los headers y defaults para ESTE módulo:
  preferencesStore.setAllModuleHeaders(moduleName, HEADERS);
  preferencesStore.setDefaultModuleHeaders(
    moduleName,
    DEFAULT_HEADER_ORDER
      .map(value => HEADERS.find(header => header.value === value))
      .filter(Boolean) as typeof HEADERS
  );
});

const isDominiomedios =
  typeof window !== 'undefined' && window.location.hostname === 'crm.dominiomedios.com';


const exportPdfAndDownload = (item: any) => {
  if (isDominiomedios) {
    quotationsStore.exportPdfAndDownload(item?.id);
  } else {
    console.log(
      '[exportPdfAndDownload] Acción deshabilitada por falta de personalización'
    );
  }
};

function applyTransforms(fields: any, raw: Record<string, any>) {
  const payload: Record<string, any> = {};
  for (const f of fields) {
    const val = raw?.[f.value];
    if (typeof f.transform === 'function') {
      Object.assign(payload, f.transform(val, raw));
    } else if (f.isJsonArray) {
      payload[f.value] = Array.isArray(val) ? val : (val ? [val] : []);
    } else {
      payload[f.value] = val;
    }
  }
  return payload;
}

const openConfirmConversion = async (): Promise<Record<string, any>> => {
  // Abre el EvidenceForm de forma PROMISE (como en tu Kanban)
  const formData = await floatFormStore.openFormWithPromise({
    component: 'EvidenceForm',
    formTitle: 'Adjunta pauta, orden de compra y condiciones de facturación',
    formWidth: 800,
    props: {
      fields: CONVERT_TO_SALE_FIELDS,
      formTitle: 'Adjunta pauta, orden de compra y condiciones de facturación',
    },
  });
  return applyTransforms(CONVERT_TO_SALE_FIELDS, formData);
};

const convertQuotation = async (item:any) => {
  try {
    if(isDominiomedios){
      if (item?.subject?.type === 'clients') {
        const extra = await openConfirmConversion();
        const additional_data = {
          billing: {
            organization: extra.organization,
            invoice_date: extra.invoice_date,
            note: extra.note,
          },
          pauta: extra.pauta,
          orden_de_compra: extra.orden_de_compra
        };
        await quotationsStore.convertQuotation(item.id, undefined, additional_data);
        return;
      }
      if (item?.subject?.type === 'leads') {
        const { openForm } = useFloatFormStore();
        openForm({
          component: 'CreateForm',
          formTitle: 'Para convertir la cotización en venta es necesario convertir el prospecto en cliente',
          formWidth: '1080',
          module: 'Clients',
          props: {
            // Puedes mapear campos del lead aquí si quieres precargar info:
            initialValues: {
              commercial_name: item.subject.attributes?.name ?? '',
              legal_name: item.subject.attributes?.legal_name ?? '',
              email: item.subject.attributes?.email ?? '',
              phone: item.subject.attributes?.phone ?? '',
              // ...mapear los campos que te sirvan
            },
            onCreate: async (clientData:any) => {
              const extra = await openConfirmConversion();
              const additional_data = {
                billing: {
                  organization: extra.organization,
                  invoice_date: extra.invoice_date,
                  note: extra.note,
                },
                pauta: extra.pauta,
                orden_de_compra: extra.orden_de_compra
              };
              await quotationsStore.convertQuotation(item.id, clientData, additional_data);
              return;
            }
          }
        });
      }
    }else{
      if (item?.subject?.type === 'clients') {
        await quotationsStore.convertQuotation(item.id, undefined, undefined);
        return;
      }
      if (item?.subject?.type === 'leads') {
        const { openForm } = useFloatFormStore();
        openForm({
          component: 'CreateForm',
          formTitle: 'Para convertir la cotización en venta es necesario convertir el prospecto en cliente',
          formWidth: '1080',
          module: 'Clients',
          props: {
            // Puedes mapear campos del lead aquí si quieres precargar info:
            initialValues: {
              commercial_name: item.subject.attributes?.name ?? '',
              legal_name: item.subject.attributes?.legal_name ?? '',
              email: item.subject.attributes?.email ?? '',
              phone: item.subject.attributes?.phone ?? '',
              // ...mapear los campos que te sirvan
            },
            onCreate: async (clientData:any) => {
              await quotationsStore.convertQuotation(item.id, clientData, undefined);
              return;
            }
          }
        });
      }
    }
  }catch (e) {
    // Manejo de error centralizado (tu composable de alerts ya lo puede capturar)
    console.error('Error al convertir la cotización:', e);
  }
};


const authorizeQuotation = (item:any) => {
  quotationsStore.authorizeQuotation({quotation_id:item?.id});
};

const authorizeQuotationItem = (item:any) => {
  quotationsStore.authorizeQuotationItem(item.id);
};

const rowActionHandlers = {
  exportPdfAndDownload,
  convertQuotation,
  authorizeQuotation,
  authorizeQuotationItem
};

const data = computed(() => quotationsStore.quotations);

</script>
